import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useGuestBooking } from '@/store/guestBooking';
import { formatGBP } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
function PasswordStrength({ password }) {
    if (!password)
        return null;
    const score = [
        password.length >= 8,
        /[A-Z]/.test(password),
        /[a-z]/.test(password),
        /[0-9]/.test(password),
    ].filter(Boolean).length;
    const label = score <= 2 ? 'Weak' : score === 3 ? 'Fair' : 'Strong';
    const colour = score <= 2 ? 'bg-red-400' : score === 3 ? 'bg-yellow-400' : 'bg-brand-green';
    return (_jsxs("div", { className: "mt-1", children: [_jsx("div", { className: "flex gap-1 mb-0.5", children: [1, 2, 3, 4].map((i) => (_jsx("div", { className: `h-1 flex-1 rounded-full ${i <= score ? colour : 'bg-gray-200'}` }, i))) }), _jsx("p", { className: "text-xs text-gray-500", children: label })] }));
}
export default function PostBookingPrompt() {
    const navigate = useNavigate();
    const { basket, contact, confirmedBookingIds, reset } = useGuestBooking((s) => ({
        basket: s.basket,
        contact: s.contact,
        confirmedBookingIds: s.confirmedBookingIds,
        reset: s.reset,
    }));
    const [password, setPassword] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    // If arrived here without a completed payment, redirect
    if (!contact || confirmedBookingIds.length === 0) {
        navigate('/book', { replace: true });
        return null;
    }
    const totalPence = basket.reduce((s, i) => s + i.amount_pence, 0);
    async function handleCreateAccount(e) {
        e.preventDefault();
        if (!contact)
            return;
        if (password !== confirmPw) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 8 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
            setError('Password must be at least 8 characters with uppercase, lowercase, and a number.');
            return;
        }
        setCreating(true);
        setError(null);
        // Sign up with 'open' role
        const { data, error: signUpError } = await supabase.auth.signUp({
            email: contact.email,
            password,
            options: {
                data: { role: 'open' },
            },
        });
        if (signUpError) {
            // If user already exists, prompt to log in
            if (signUpError.message.includes('already registered') || signUpError.message.includes('already exists')) {
                setError('An account with this email already exists. Please log in to view your booking.');
            }
            else {
                setError(signUpError.message);
            }
            setCreating(false);
            return;
        }
        if (data.user) {
            // Update profile with contact details
            await supabase.from('profiles').update({
                full_name: contact.name,
                phone: contact.phone,
                role: 'open',
            }).eq('id', data.user.id);
            // Associate confirmed bookings with the new user
            if (confirmedBookingIds.length > 0) {
                await supabase
                    .from('open_service_bookings')
                    .update({ user_id: data.user.id })
                    .in('id', confirmedBookingIds);
            }
        }
        setDone(true);
        reset();
        setCreating(false);
    }
    function handleSkip() {
        reset();
        navigate('/');
    }
    // Success state after account creation
    if (done) {
        return (_jsx("div", { className: "min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 text-center", children: _jsxs("div", { className: "bg-white rounded-2xl border border-gray-200 p-8 max-w-sm w-full", children: [_jsx("div", { className: "text-4xl mb-4", children: "\u2713" }), _jsx("h1", { className: "text-xl font-bold text-gray-900 mb-2", children: "Account created!" }), _jsx("p", { className: "text-gray-500 text-sm mb-6", children: "Check your email to confirm your account, then log in to view your booking history." }), _jsx("button", { onClick: () => navigate('/login'), className: "w-full py-3 bg-brand-green text-white font-medium rounded-lg hover:bg-brand-green/90", children: "Log in" })] }) }));
    }
    return (_jsx("div", { className: "min-h-screen bg-brand-cream", children: _jsxs("main", { className: "max-w-xl mx-auto px-4 py-12", children: [_jsxs("div", { className: "bg-green-50 border border-green-200 rounded-xl p-5 mb-8 text-center", children: [_jsx("p", { className: "text-brand-green font-semibold text-lg mb-1", children: "Booking confirmed!" }), _jsx("p", { className: "text-brand-green text-sm", children: "Check your email and phone for your confirmation." })] }), _jsxs("div", { className: "bg-white border border-gray-200 rounded-xl p-4 mb-8", children: [basket.map((item, i) => (_jsxs("div", { className: "flex justify-between text-sm py-1", children: [_jsxs("span", { className: "text-gray-700", children: [item.service_name, " \u2014 ", format(parseISO(item.booking_date), 'd MMMM yyyy'), item.booking_time ? ` at ${item.booking_time}` : ''] }), _jsx("span", { className: "font-medium", children: formatGBP(item.amount_pence) })] }, i))), _jsxs("div", { className: "flex justify-between font-semibold text-gray-900 pt-2 mt-1 border-t border-gray-100", children: [_jsx("span", { children: "Total paid" }), _jsx("span", { children: formatGBP(totalPence) })] })] }), _jsxs("div", { className: "bg-white border border-gray-200 rounded-xl p-6", children: [_jsx("h2", { className: "font-semibold text-gray-900 mb-1", children: "Book faster next time" }), _jsx("p", { className: "text-sm text-gray-500 mb-5", children: "Create a free account to save your details and view your booking history. Just set a password \u2014 we already have the rest." }), _jsxs("form", { onSubmit: handleCreateAccount, className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Password" }), _jsx("input", { type: "password", required: true, value: password, onChange: (e) => setPassword(e.target.value), className: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40" }), _jsx(PasswordStrength, { password: password })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Confirm password" }), _jsx("input", { type: "password", required: true, value: confirmPw, onChange: (e) => setConfirmPw(e.target.value), className: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40" }), confirmPw && password !== confirmPw && (_jsx("p", { className: "text-xs text-red-600 mt-1", children: "Passwords do not match." }))] }), error && _jsx("p", { className: "text-sm text-red-600", children: error }), _jsx("button", { type: "submit", disabled: creating || !password || password !== confirmPw, className: "w-full py-3 bg-brand-green text-white font-medium rounded-lg hover:bg-brand-green/90 disabled:opacity-50", children: creating ? 'Creating account…' : 'Create Account' })] }), _jsx("button", { onClick: handleSkip, className: "w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700", children: "No thanks \u2014 skip" })] })] }) }));
}
